import React, { useEffect } from 'react'
import { useFormik } from 'formik';
import { checkValidArray, handleServerResponse, saveEditForm, useSafeFormikContext } from 'GenericForms/Helper';
import { RootState } from 'index';
import { useSelector } from 'react-redux';
import { VesselMachineryConstant } from 'shared/constants';
import * as Yup from 'yup'
import env from 'environment_system/env_system';
import { commonValidationMessages } from 'Components/ValidationErrorMessages';
import apiGlobal from 'global/api.global';
import { queryKeyes } from 'shared/queryKeys';
import { Form, Card, CardHeader, CardBody, Row, Col, Label, Input, CardFooter, Button } from 'reactstrap';
import ErrorTooltip from 'Components/ErrorTooltip';
import FormValuesDebug from 'utils/debugTools/FormValuesDebug';
import { useEditReport } from 'VesselReportingInfo/EditCompletedReports/EditReportProvider';
import { queryClient } from 'react-query/queryClient';

const OtherDetails = () => {
    /** State variables start */
    const { editReportBool, setPreviousTabIndex, previousEnggTabIndex, setErrorMessage, toggleDynamicTabs } = useEditReport();
    const { VesselID, ReportID, VoyageID, CurrentReport, ConfigurationLimits } = useSelector((state: RootState) => state.Reporting);
    const formikContext = useSafeFormikContext<any>();
    const editValues = formikContext?.values;
    const setEditValues = formikContext?.setValues;
    const editInitialValues = formikContext?.initialValues;
    const editSubmit = formikContext?.submitForm;
    const editIsSubmitting = formikContext?.isSubmitting;
    const mainEngines = ConfigurationLimits?.vessel_machinery_list?.filter((item: any) => item.machinery_precedence_id === VesselMachineryConstant.MAIN_ENGINE);
    const auxiliaryEngines = ConfigurationLimits?.vessel_machinery_list?.filter((item: any) => item.machinery_precedence_id === VesselMachineryConstant.AUXILIARY_ENGINE);
    /** State variables end */

    /** Assign values to formik's initial object */
    const getInitialValues = () => {
        let arr: any[] = [];
        if (editReportBool) {
            if (checkValidArray(editInitialValues?.chief_engineer?.arya_tankers_additional_details)) {
                return editInitialValues?.chief_engineer?.arya_tankers_additional_details;
            }
        } else if (checkValidArray(CurrentReport?.data?.chief_engineer?.arya_tankers_additional_details)) {
            return CurrentReport?.data?.chief_engineer?.arya_tankers_additional_details;
        } else {
            if (checkValidArray(mainEngines)) {
                mainEngines?.forEach((engine: any) => {
                    arr.push({
                        vessel_machinery: engine.id,
                        vessel_machinery_name: engine.vessel_machinery_name,
                        machinery_precedence_id: engine.machinery_precedence_id,
                        me_exh_max_temp: null,
                        me_exh_min_temp: null,
                        me_tc_in: null,
                        me_tc_out: null,
                        me_scavenge_temp: null,
                        me_scavenge_pressure: null,
                        me_air_cooler_pressure_drop: null,
                        me_sump_sounding: null,
                        me_sump_volume: null,
                        load: null,
                        egb_exh_max_temp: null,
                        egb_press_drop: null,
                        vessel: VesselID,
                        voyage_information: VoyageID,
                        vessel_reporting_information: ReportID,
                    });
                });
            }
            if (checkValidArray(auxiliaryEngines)) {
                auxiliaryEngines?.forEach((engine: any) => {
                    arr.push({
                        vessel_machinery: engine.id,
                        vessel_machinery_name: engine.vessel_machinery_name,
                        machinery_precedence_id: engine.machinery_precedence_id,
                        me_exh_max_temp: null,
                        me_exh_min_temp: null,
                        me_tc_in: null,
                        me_tc_out: null,
                        me_scavenge_temp: null,
                        me_scavenge_pressure: null,
                        me_air_cooler_pressure_drop: null,
                        me_sump_sounding: null,
                        me_sump_volume: null,
                        load: null,
                        egb_exh_max_temp: null,
                        egb_press_drop: null,
                        vessel: VesselID,
                        voyage_information: VoyageID,
                        vessel_reporting_information: ReportID,
                    });
                });
            }
            arr.push({
                vessel_machinery: null,
                vessel_machinery_name: null,
                machinery_precedence_id: null,
                me_exh_max_temp: null,
                me_exh_min_temp: null,
                me_tc_in: null,
                me_tc_out: null,
                me_scavenge_temp: null,
                me_scavenge_pressure: null,
                me_air_cooler_pressure_drop: null,
                me_sump_sounding: null,
                me_sump_volume: null,
                load: null,
                egb_exh_max_temp: null,
                egb_press_drop: null,
                vessel: VesselID,
                voyage_information: VoyageID,
                vessel_reporting_information: ReportID,
            })
            return arr;
        }
    }

    /** Formik object of Other Details */
    const OtherDetailsFormik: any = useFormik<any>({
        enableReinitialize: true,
        initialValues: getInitialValues(),
        validationSchema: env?.form_validation === true ?
            Yup.array(
                Yup.object().shape({
                    vessel_machinery: Yup.number().nullable(),
                    me_exh_max_temp: Yup.string().matches(/^\d{0,3}(\.\d{1,4})?$/, `${commonValidationMessages.before3after4}`).nullable(),
                    me_exh_min_temp: Yup.string().matches(/^\d{0,3}(\.\d{1,4})?$/, `${commonValidationMessages.before3after4}`).nullable(),
                    me_tc_in: Yup.string().matches(/^\d{0,3}(\.\d{1,4})?$/, `${commonValidationMessages.before3after4}`).nullable(),
                    me_tc_out: Yup.string().matches(/^\d{0,3}(\.\d{1,4})?$/, `${commonValidationMessages.before3after4}`).nullable(),
                    me_scavenge_temp: Yup.string().matches(/^\d{0,3}(\.\d{1,4})?$/, `${commonValidationMessages.before3after4}`).nullable(),
                    me_scavenge_pressure: Yup.string().matches(/^\d{0,3}(\.\d{1,4})?$/, `${commonValidationMessages.before3after4}`).nullable(),
                    me_air_cooler_pressure_drop: Yup.string().matches(/^\d{0,3}(\.\d{1,4})?$/, `${commonValidationMessages.before3after4}`).nullable(),
                    me_sump_sounding: Yup.string().nullable(),
                    me_sump_volume: Yup.string().nullable(),
                    load: Yup.string().matches(/^\d{0,4}(\.\d{1,4})?$/, `${commonValidationMessages.before4after4}`).nullable(),
                    egb_exh_max_temp: Yup.string().matches(/^\d{0,3}(\.\d{1,4})?$/, `${commonValidationMessages.before3after4}`).nullable(),
                    egb_press_drop: Yup.string().matches(/^\d{0,4}(\.\d{1,4})?$/, `${commonValidationMessages.before4after4}`).nullable(),
                })
            ) : null,
        onSubmit: async (values, actions) => {
            const responseArray: any = [];
            if (env?.form_validation === false) {
                setErrorMessage(null);
                toggleDynamicTabs(previousEnggTabIndex + 1);
            } else {
                if (checkValidArray(CurrentReport?.data?.chief_engineer?.arya_tankers_additional_details)) {
                    values?.forEach((value: any) => {
                        responseArray.push(apiGlobal.put(queryKeyes.vessel.AryaTankersAdditionalDetails.url(value?.id), value))
                    })
                } else {
                    responseArray.push(apiGlobal.post(queryKeyes.vessel.AryaTankersAdditionalDetails.url(), values))
                }
            }
            await handleServerResponse(responseArray).then(async (res) => {
                if (res === true) {
                    await queryClient.invalidateQueries(queryKeyes.vessel.EconMEAdditionalObject.key);
                    if (env?.form_validation === true) {
                        setErrorMessage(null);
                        toggleDynamicTabs(previousEnggTabIndex + 1);
                    }
                } else {
                    setErrorMessage(res)
                }
                actions.setSubmitting(false);
            })
            actions.setSubmitting(true);
        },
    });

    /** UseEffect */
    useEffect(() => {
        if (editReportBool && checkValidArray(editValues?.chief_engineer?.arya_tankers_additional_details)) {
            setEditValues({
                ...editValues,
                chief_engineer: {
                    ...editValues?.chief_engineer,
                    arya_tankers_additional_details: [OtherDetailsFormik?.values],
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [OtherDetailsFormik?.values]);
    /** useEffect end */

    return (
        <React.Fragment>
            <Form onSubmit={OtherDetailsFormik?.handleSubmit} autoComplete='off' noValidate>
                <Card className='p-0 mb-2 border-0'>
                    <CardBody className='p-0'>
                        <Card className='p-0 mb-2 border-0'>
                            <CardHeader className='p-2 border-bottom-0'>
                                <h5>Main engine(s)</h5>
                            </CardHeader>
                            <CardBody className='p-0'>
                                {OtherDetailsFormik?.values && OtherDetailsFormik?.values
                                    ?.filter((item: any) => item.machinery_precedence_id === VesselMachineryConstant.MAIN_ENGINE)
                                    ?.map((value: any, index: number) => {
                                        return (
                                            <React.Fragment key={index}>
                                                <Card className='card-bottom-0 p-0 mb-0'>
                                                    <CardHeader className='p-2'>
                                                        <h6 className='mb-0'>{value?.vessel_machinery_name}</h6>
                                                    </CardHeader>
                                                    <CardBody className='px-2 py-0'>
                                                        <Row className='mb-2 mt-2'>
                                                            <Col sm={3}>
                                                                <Label className="mb-0" htmlFor={`me_exh_max_${index}_temp`}>Max exhaust temperature</Label>
                                                                <div className='input-group'>
                                                                    <Input
                                                                        onFocus={(e) => e.target.select()}
                                                                        type="text"
                                                                        id={`me_exh_max_${index}_temp`}
                                                                        name={`${index}.me_exh_max_temp`}
                                                                        className="form-cntrol max-width-7 text-right"
                                                                        onChange={(e: any) => {
                                                                            OtherDetailsFormik?.handleChange(e);
                                                                        }}
                                                                        onBlur={(e: any) => {
                                                                            OtherDetailsFormik?.handleChange(e);
                                                                            OtherDetailsFormik?.handleBlur(e);
                                                                        }}
                                                                        value={value?.me_exh_max_temp}
                                                                    />
                                                                    <div className="input-group-text"><sup>o</sup>C</div>
                                                                </div>
                                                                {env?.form_validation === true && OtherDetailsFormik?.errors && OtherDetailsFormik?.errors[index]?.me_exh_max_temp &&
                                                                    <ErrorTooltip
                                                                        target={`me_exh_max_${index}_temp`}
                                                                        message={OtherDetailsFormik?.errors[index]?.me_exh_max_temp}
                                                                        open={OtherDetailsFormik?.errors[index]?.me_exh_max_temp && OtherDetailsFormik?.touched[index]?.me_exh_max_temp}
                                                                    />
                                                                }
                                                            </Col>
                                                            <Col sm={3}>
                                                                <Label className="mb-0" htmlFor={`me_exh_min_${index}_temp`}>Min exhaust temperature</Label>
                                                                <div className='input-group'>
                                                                    <Input
                                                                        onFocus={(e) => e.target.select()}
                                                                        type="text"
                                                                        id={`me_exh_min_${index}_temp`}
                                                                        name={`${index}.me_exh_min_temp`}
                                                                        className="form-cntrol min-width-7 text-right"
                                                                        onChange={(e: any) => {
                                                                            OtherDetailsFormik?.handleChange(e);
                                                                        }}
                                                                        onBlur={(e: any) => {
                                                                            OtherDetailsFormik?.handleChange(e);
                                                                            OtherDetailsFormik?.handleBlur(e);
                                                                        }}
                                                                        value={value?.me_exh_min_temp}
                                                                    />
                                                                    <div className="input-group-text"><sup>o</sup>C</div>
                                                                </div>
                                                                {env?.form_validation === true && OtherDetailsFormik?.errors && OtherDetailsFormik?.errors[index]?.me_exh_min_temp &&
                                                                    <ErrorTooltip
                                                                        target={`me_exh_min_${index}_temp`}
                                                                        message={OtherDetailsFormik?.errors[index]?.me_exh_min_temp}
                                                                        open={OtherDetailsFormik?.errors[index]?.me_exh_min_temp && OtherDetailsFormik?.touched[index]?.me_exh_min_temp}
                                                                    />
                                                                }
                                                            </Col>
                                                            <Col sm={3}>
                                                                <Label className="mb-0" htmlFor={`me_tc_in${index}_temp`}>T/C inlet temperature</Label>
                                                                <div className='input-group'>
                                                                    <Input
                                                                        onFocus={(e) => e.target.select()}
                                                                        type="text"
                                                                        id={`me_tc_in${index}_temp`}
                                                                        name={`${index}.me_tc_in`}
                                                                        className="form-cntrol max-width-7 text-right"
                                                                        onChange={(e: any) => {
                                                                            OtherDetailsFormik?.handleChange(e);
                                                                        }}
                                                                        onBlur={(e: any) => {
                                                                            OtherDetailsFormik?.handleChange(e);
                                                                            OtherDetailsFormik?.handleBlur(e);
                                                                        }}
                                                                        value={value?.me_tc_in}
                                                                    />
                                                                    <div className="input-group-text"><sup>o</sup>C</div>
                                                                </div>
                                                                {env?.form_validation === true && OtherDetailsFormik?.errors && OtherDetailsFormik?.errors[index]?.me_tc_in &&
                                                                    <ErrorTooltip
                                                                        target={`me_tc_in${index}_temp`}
                                                                        message={OtherDetailsFormik?.errors[index]?.me_tc_in}
                                                                        open={OtherDetailsFormik?.errors[index]?.me_tc_in && OtherDetailsFormik?.touched[index]?.me_tc_in}
                                                                    />
                                                                }
                                                            </Col>
                                                            <Col sm={3}>
                                                                <Label className="mb-0" htmlFor={`me_tc_out${index}_temp`}>T/C outlet temperature</Label>
                                                                <div className='input-group'>
                                                                    <Input
                                                                        onFocus={(e) => e.target.select()}
                                                                        type="text"
                                                                        id={`me_tc_out${index}_temp`}
                                                                        name={`${index}.me_tc_out`}
                                                                        className="form-cntrol max-width-7 text-right"
                                                                        onChange={(e: any) => {
                                                                            OtherDetailsFormik?.handleChange(e);
                                                                        }}
                                                                        onBlur={(e: any) => {
                                                                            OtherDetailsFormik?.handleChange(e);
                                                                            OtherDetailsFormik?.handleBlur(e);
                                                                        }}
                                                                        value={value?.me_tc_out}
                                                                    />
                                                                    <div className="input-group-text"><sup>o</sup>C</div>
                                                                </div>
                                                                {env?.form_validation === true && OtherDetailsFormik?.errors && OtherDetailsFormik?.errors[index]?.me_tc_out &&
                                                                    <ErrorTooltip
                                                                        target={`me_tc_out${index}_temp`}
                                                                        message={OtherDetailsFormik?.errors[index]?.me_tc_out}
                                                                        open={OtherDetailsFormik?.errors[index]?.me_tc_out && OtherDetailsFormik?.touched[index]?.me_tc_out}
                                                                    />
                                                                }
                                                            </Col>
                                                        </Row>
                                                        <Row className='mb-2'>
                                                            <Col sm={3}>
                                                                <Label className="mb-0" htmlFor={`me_scavenge_${index}_temp`}>Scavenge temperature</Label>
                                                                <div className='input-group'>
                                                                    <Input
                                                                        onFocus={(e) => e.target.select()}
                                                                        type="text"
                                                                        id={`me_scavenge_${index}_temp`}
                                                                        name={`${index}.me_scavenge_temp`}
                                                                        className="form-cntrol max-width-7 text-right"
                                                                        onChange={(e: any) => {
                                                                            OtherDetailsFormik?.handleChange(e);
                                                                        }}
                                                                        onBlur={(e: any) => {
                                                                            OtherDetailsFormik?.handleChange(e);
                                                                            OtherDetailsFormik?.handleBlur(e);
                                                                        }}
                                                                        value={value?.me_scavenge_temp}
                                                                    />
                                                                    <div className="input-group-text"><sup>o</sup>C</div>
                                                                </div>
                                                                {env?.form_validation === true && OtherDetailsFormik?.errors && OtherDetailsFormik?.errors[index]?.me_scavenge_temp &&
                                                                    <ErrorTooltip
                                                                        target={`me_scavenge_${index}_temp`}
                                                                        message={OtherDetailsFormik?.errors[index]?.me_scavenge_temp}
                                                                        open={OtherDetailsFormik?.errors[index]?.me_scavenge_temp && OtherDetailsFormik?.touched[index]?.me_scavenge_temp}
                                                                    />
                                                                }
                                                            </Col>
                                                            <Col sm={3}>
                                                                <Label className="mb-0" htmlFor={`me_scavenge_${index}_pressure`}>Scavenge pressure</Label>
                                                                <div className='input-group'>
                                                                    <Input
                                                                        onFocus={(e) => e.target.select()}
                                                                        type="text"
                                                                        id={`me_scavenge_${index}_pressure`}
                                                                        name={`${index}.me_scavenge_pressure`}
                                                                        className="form-cntrol max-width-7 text-right"
                                                                        onChange={(e: any) => {
                                                                            OtherDetailsFormik?.handleChange(e);
                                                                        }}
                                                                        onBlur={(e: any) => {
                                                                            OtherDetailsFormik?.handleChange(e);
                                                                            OtherDetailsFormik?.handleBlur(e);
                                                                        }}
                                                                        value={value?.me_scavenge_pressure}
                                                                    />
                                                                    <div className="input-group-text">bar</div>
                                                                </div>
                                                                {env?.form_validation === true && OtherDetailsFormik?.errors && OtherDetailsFormik?.errors[index]?.me_scavenge_pressure &&
                                                                    <ErrorTooltip
                                                                        target={`me_scavenge_${index}_pressure`}
                                                                        message={OtherDetailsFormik?.errors[index]?.me_scavenge_pressure}
                                                                        open={OtherDetailsFormik?.errors[index]?.me_scavenge_pressure && OtherDetailsFormik?.touched[index]?.me_scavenge_pressure}
                                                                    />
                                                                }
                                                            </Col>
                                                            <Col sm={3}>
                                                                <Label className="mb-0" htmlFor={`me_sump_${index}_sounding`}>Sump sounding</Label>
                                                                <div className='input-group'>
                                                                    <Input
                                                                        onFocus={(e) => e.target.select()}
                                                                        type="text"
                                                                        id={`me_sump_${index}_sounding`}
                                                                        name={`${index}.me_sump_sounding`}
                                                                        className="form-cntrol max-width-7 text-right"
                                                                        onChange={(e: any) => {
                                                                            OtherDetailsFormik?.handleChange(e);
                                                                        }}
                                                                        onBlur={(e: any) => {
                                                                            OtherDetailsFormik?.handleChange(e);
                                                                            OtherDetailsFormik?.handleBlur(e);
                                                                        }}
                                                                        value={value?.me_sump_sounding}
                                                                    />
                                                                    <div className="input-group-text">cm</div>
                                                                </div>
                                                                {env?.form_validation === true && OtherDetailsFormik?.errors && OtherDetailsFormik?.errors[index]?.me_sump_sounding &&
                                                                    <ErrorTooltip
                                                                        target={`me_sump_${index}_sounding`}
                                                                        message={OtherDetailsFormik?.errors[index]?.me_sump_sounding}
                                                                        open={OtherDetailsFormik?.errors[index]?.me_sump_sounding && OtherDetailsFormik?.touched[index]?.me_sump_sounding}
                                                                    />
                                                                }
                                                            </Col>
                                                            <Col sm={3}>
                                                                <Label className="mb-0" htmlFor={`me_sump_${index}_volume`}>Sump sounding</Label>
                                                                <div className='input-group'>
                                                                    <Input
                                                                        onFocus={(e) => e.target.select()}
                                                                        type="text"
                                                                        id={`me_sump_${index}_volume`}
                                                                        name={`${index}.me_sump_volume`}
                                                                        className="form-cntrol max-width-7 text-right"
                                                                        onChange={(e: any) => {
                                                                            OtherDetailsFormik?.handleChange(e);
                                                                        }}
                                                                        onBlur={(e: any) => {
                                                                            OtherDetailsFormik?.handleChange(e);
                                                                            OtherDetailsFormik?.handleBlur(e);
                                                                        }}
                                                                        value={value?.me_sump_volume}
                                                                    />
                                                                    <div className="input-group-text">m<sup>3</sup></div>
                                                                </div>
                                                                {env?.form_validation === true && OtherDetailsFormik?.errors && OtherDetailsFormik?.errors[index]?.me_sump_volume &&
                                                                    <ErrorTooltip
                                                                        target={`me_sump_${index}_volume`}
                                                                        message={OtherDetailsFormik?.errors[index]?.me_sump_volume}
                                                                        open={OtherDetailsFormik?.errors[index]?.me_sump_volume && OtherDetailsFormik?.touched[index]?.me_sump_volume}
                                                                    />
                                                                }
                                                            </Col>
                                                        </Row>
                                                    </CardBody>
                                                </Card>
                                            </React.Fragment>
                                        )
                                    })}
                            </CardBody>
                        </Card>
                        <Card className='p-0 mb-2 border-0'>
                            <CardHeader className='p-2 border-bottom-0'>
                                <h5>Auxiliary engine(s)</h5>
                            </CardHeader>
                            <CardBody className='p-0'>
                                <div className="table-responsive p-0">
                                    <table className="table mb-2 mt-2">
                                        <thead className="table-light">
                                            <tr>
                                                <th className="align-middle p-2 sr-no-width">
                                                    #
                                                </th>
                                                <th className="align-middle p-2">Machinery</th>
                                                <th className="align-middle p-2 text-center">Load</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {OtherDetailsFormik?.values && OtherDetailsFormik?.values
                                                ?.filter((item: any) => item.machinery_precedence_id === VesselMachineryConstant.AUXILIARY_ENGINE)
                                                ?.map((value: any, index: number) => {
                                                    return (
                                                        <tr key={index}>
                                                            <td className="align-middle p-2 text-center">
                                                                {index + 1}
                                                            </td>
                                                            <td className="align-middle p-2">
                                                                {value?.vessel_machinery_name}
                                                            </td>
                                                            <td className="align-middle p-2 text-center">
                                                                <div className='input-group justify-content-center'>
                                                                    <Input
                                                                        onFocus={(e) => e.target.select()}
                                                                        type="text"
                                                                        id={`ae_${index}_load`}
                                                                        name={`${index}.load`}
                                                                        className="form-cntrol max-width-7 text-right"
                                                                        onChange={(e: any) => {
                                                                            OtherDetailsFormik?.handleChange(e);
                                                                        }}
                                                                        onBlur={(e: any) => {
                                                                            OtherDetailsFormik?.handleChange(e);
                                                                            OtherDetailsFormik?.handleBlur(e);
                                                                        }}
                                                                        value={value?.load}
                                                                    />
                                                                    <div className="input-group-text">kW</div>
                                                                </div>
                                                                {env?.form_validation === true && OtherDetailsFormik?.errors && OtherDetailsFormik?.errors[index]?.load &&
                                                                    <ErrorTooltip
                                                                        target={`ae_${index}_load`}
                                                                        message={OtherDetailsFormik?.errors[index]?.load}
                                                                        open={OtherDetailsFormik?.errors[index]?.load && OtherDetailsFormik?.touched[index]?.load}
                                                                    />
                                                                }
                                                            </td>
                                                        </tr>
                                                    )
                                                })
                                            }
                                        </tbody>
                                    </table>
                                </div>
                            </CardBody>
                        </Card>
                        <Card className='p-0 mb-2 border-0'>
                            <CardHeader className='p-2'>
                                <h5>EGB</h5>
                            </CardHeader>
                            <CardBody className='px-2 py-0'>
                                <Row className='mb-2 mt-2'>
                                    <Col sm={3}>
                                        <Label className="mb-0" htmlFor={`egb_${OtherDetailsFormik?.values?.length - 1}_max_temp`}>Max exhaust temperature</Label>
                                        <div className='input-group'>
                                            <Input
                                                onFocus={(e) => e.target.select()}
                                                type="text"
                                                id={`egb_${OtherDetailsFormik?.values?.length - 1}_max_temp`}
                                                name={`egb_exh_max_temp`}
                                                className="form-cntrol max-width-7 text-right"
                                                onChange={(e: any) => {
                                                    OtherDetailsFormik?.handleChange(e);
                                                }}
                                                onBlur={(e: any) => {
                                                    OtherDetailsFormik?.handleChange(e);
                                                    OtherDetailsFormik?.handleBlur(e);
                                                }}
                                                value={OtherDetailsFormik?.values[OtherDetailsFormik?.values?.length - 1]?.egb_exh_max_temp}
                                            />
                                            <div className="input-group-text"><sup>o</sup>C</div>
                                        </div>
                                        {env?.form_validation === true && OtherDetailsFormik?.errors && OtherDetailsFormik?.errors[OtherDetailsFormik?.values?.length - 1]?.egb_exh_max_temp &&
                                            <ErrorTooltip
                                                target={`egb_${OtherDetailsFormik?.values?.length - 1}_max_temp`}
                                                message={OtherDetailsFormik?.errors[OtherDetailsFormik?.values?.length - 1]?.egb_exh_max_temp}
                                                open={OtherDetailsFormik?.errors[OtherDetailsFormik?.values?.length - 1]?.egb_exh_max_temp && OtherDetailsFormik?.touched[OtherDetailsFormik?.values?.length - 1]?.egb_exh_max_temp}
                                            />
                                        }
                                    </Col>
                                    <Col sm={3}>
                                        <Label className="mb-0" htmlFor={`egb_${OtherDetailsFormik?.values?.length - 1}_press_drop`}>Max exhaust temperature</Label>
                                        <div className='input-group'>
                                            <Input
                                                onFocus={(e) => e.target.select()}
                                                type="text"
                                                id={`egb_${OtherDetailsFormik?.values?.length - 1}_press_drop`}
                                                name={`egb_press_drop`}
                                                className="form-cntrol max-width-7 text-right"
                                                onChange={(e: any) => {
                                                    OtherDetailsFormik?.handleChange(e);
                                                }}
                                                onBlur={(e: any) => {
                                                    OtherDetailsFormik?.handleChange(e);
                                                    OtherDetailsFormik?.handleBlur(e);
                                                }}
                                                value={OtherDetailsFormik?.values[OtherDetailsFormik?.values?.length - 1]?.egb_press_drop}
                                            />
                                            <div className="input-group-text"><sup>o</sup>C</div>
                                        </div>
                                        {env?.form_validation === true && OtherDetailsFormik?.errors && OtherDetailsFormik?.errors[OtherDetailsFormik?.values?.length - 1]?.egb_press_drop &&
                                            <ErrorTooltip
                                                target={`egb_${OtherDetailsFormik?.values?.length - 1}_press_drop`}
                                                message={OtherDetailsFormik?.errors[OtherDetailsFormik?.values?.length - 1]?.egb_press_drop}
                                                open={OtherDetailsFormik?.errors[OtherDetailsFormik?.values?.length - 1]?.egb_press_drop && OtherDetailsFormik?.touched[OtherDetailsFormik?.values?.length - 1]?.egb_press_drop}
                                            />
                                        }
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </CardBody>
                    <CardFooter className='p-2 py-3'>
                        <Row className="ele_row1">
                            <div className="d-flex flex-wrap gap-5">
                                <Button
                                    type={editReportBool ? "button" : "submit"}
                                    color="primary"
                                    className="btn_size_cstm pos-end"
                                    disabled={OtherDetailsFormik?.isSubmitting || editIsSubmitting}
                                    onClick={async () => {
                                        const validationErrors = await OtherDetailsFormik.validateForm();
                                        if (editReportBool && Object.keys(validationErrors).length === 0) {
                                            saveEditForm(
                                                editSubmit,
                                                toggleDynamicTabs,
                                                previousEnggTabIndex,
                                                setPreviousTabIndex
                                            );
                                        }
                                    }}
                                >
                                    {(OtherDetailsFormik?.isSubmitting || editIsSubmitting) &&
                                        <i className="ms-2 spinner-border spinner-border-sm text-light me-2" />
                                    }
                                    Next <i className="bx bx-chevron-right ms-1" />
                                </Button>
                                <Button type="button" color="primary" className="btn_size_cstm" onClick={() => {
                                    OtherDetailsFormik.setErrors({})
                                    OtherDetailsFormik.errors = {};
                                    toggleDynamicTabs(previousEnggTabIndex - 1);
                                    setErrorMessage(null)
                                }}>
                                    <i className="bx bx-chevron-left me-1" />Previous
                                </Button>
                            </div>
                        </Row>
                    </CardFooter>
                </Card>
                <FormValuesDebug values={[OtherDetailsFormik?.values, OtherDetailsFormik?.errors, OtherDetailsFormik?.initialValues]} />
            </Form>
        </React.Fragment>
    )
}

export default OtherDetails